import * as React from 'react'
import { cn } from '../../utils/cn'

/**
 * JPG Academy Gold Divider
 *
 * A decorative separator inspired by classical manuscript illumination.
 * The gradient fades at the edges for an elegant, liturgical feel.
 *
 * Usage:
 *   <GoldDivider />
 *   <GoldDivider thickness={2} className="my-8" />
 *   <GoldDivider withOrnament />
 */

export interface GoldDividerProps {
	className?: string
	/** Line thickness in pixels */
	thickness?: number
	/** Show a small diamond ornament in the center */
	withOrnament?: boolean
	/** Opacity (0-1) */
	opacity?: number
}

export function GoldDivider({
	className,
	thickness = 1,
	withOrnament = false,
	opacity = 0.6,
}: GoldDividerProps) {
	const lineStyle = {
		height: `${thickness}px`,
		background: `linear-gradient(
			90deg,
			transparent 0%,
			rgba(196, 151, 42, ${opacity}) 15%,
			rgba(196, 151, 42, ${opacity}) 85%,
			transparent 100%
		)`,
	}

	return (
		<div
			className={cn('relative flex items-center w-full', className)}
			role="separator"
		>
			{withOrnament ? (
				<>
					<div className="flex-1" style={{ ...lineStyle, background: `linear-gradient(90deg, transparent 0%, rgba(196, 151, 42, ${opacity}) 40%, rgba(196, 151, 42, ${opacity}) 100%)` }} />
					<div className="mx-3 flex-shrink-0">
						<svg
							width="12"
							height="12"
							viewBox="0 0 12 12"
							fill="none"
							style={{ opacity }}
						>
							<path
								d="M6 0L7.5 4.5L12 6L7.5 7.5L6 12L4.5 7.5L0 6L4.5 4.5L6 0Z"
								fill="currentColor"
								className="text-gold"
							/>
						</svg>
					</div>
					<div className="flex-1" style={{ ...lineStyle, background: `linear-gradient(90deg, rgba(196, 151, 42, ${opacity}) 0%, rgba(196, 151, 42, ${opacity}) 60%, transparent 100%)` }} />
				</>
			) : (
				<div className="flex-1" style={lineStyle} />
			)}
		</div>
	)
}
